/**
 * @typedef {import("../../opentable.js").OTConfirmPayload} OTConfirmPayload
 * @typedef { Pick<OTConfirmPayload, "first_name" | "last_name" | "email_address" | "phone_number" | "phone_country_code" | "special_request" > } DetailFormEntries
 */
import { useEffect, useRef } from "preact/compat";
import { html } from "htm/preact";
import { useAppContext } from "src/services/context";
import BaseButton from "../button/base-button.js";
import LiveAnnouncer from "../live-announcer/live-announcer.js";
import {createReservation, fetchOTExperiences, lockReservation} from "src/services/ot-api.js";
import { batch, useSignal } from "@preact/signals";
import DetailsFormTimer from "./timer.js";
import ReservationSummary from "../summary/summary.js";
import settings from "../../../../services/settings.js";
import { formatPhoneNumber, trackEvent } from "src/services/helpers.js";
import { decode } from "he";

const RESERVATION_TYPES = {
    ["default"]: "standard",
};
/**
 * Formats OT reservation types to GTM-friendly version. Over-engineered but
 * easily extensible if needed in the future.
 *
 * @default
 * @param {string} reservationType
 * @returns string
 */
function formatReservationType(reservationType) {
    const result = RESERVATION_TYPES[reservationType] ?? reservationType;
    if (!result) {
        return RESERVATION_TYPES["default"];
    }
    return result;
}

/**
 * @typedef {object} QueryFormProps
 * @prop {boolean} isVisible
 * @param {QueryFormProps} props
 * @returns {import("preact").VNode}
 */
function DetailsForm(props) {
    const state = useAppContext();
    const isLoading = useSignal(false);
    const isFetchingExperiences = useSignal(false);
    const isError = useSignal(false);

    const detailsRef = useRef(null);

    const experiences = useSignal([]);
    const selectedExperience = useSignal(undefined);

    const canShowForm = selectedExperience.value !== undefined;

    const restaurantTitle =
        state.selectedRestaurant.value?.title?.rendered
            ? decode(state.selectedRestaurant.value.title.rendered)
            : "";

    const restaurantAddress =
        state.selectedRestaurant.value?.acf?.street_address ?? "";

    async function lockWithExperience(experience) {
        isLoading.value = true;
        isError.value = false;

        try {
            const locked = await lockReservation({
                party_size: state.partySize.value.toString(),
                rid: state.selectedLocationRid.value,
                reservation_attribute: state.selectedSeating.value,
                date_time: state.selectedTime.value,
                experience_id: experience?.experience_id ?? null,
                experience_version: experience?.version ?? null,
            });

            batch(() => {
                selectedExperience.value = experience;
                state.reservationToken.value = locked.reservation_token;

                const timer = new Date();
                timer.setTime(
                    timer.getTime() +
                    settings.confirmationTimeLimitMinutes * 60 * 1000
                );
                state.pendingTimer.value = timer;

            });
        } catch {
            isError.value = true;
        } finally {
            isLoading.value = false;
        }
    }

    useEffect(() => {
        if (!props.isVisible) return;
        if (selectedExperience.value === undefined) return;
    
        setTimeout(() => {
            detailsRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }, 50);
    
        setTimeout(() => {
            document.getElementById("first_name")?.focus();
    
            // Wait for NVDA to finish announcing the focused field.
            setTimeout(() => {
                state.liveAnnouncement.value = "";
    
                setTimeout(() => {
                    state.liveAnnouncement.value =
                        `You have ${settings.confirmationTimeLimitMinutes} minutes to complete your reservation.`;
                }, 50);
    
            }, 700);
    
        }, 150);
    
    }, [props.isVisible, selectedExperience.value]);

    useEffect(() => {
        if (!props.isVisible) return;
        if (selectedExperience.value !== undefined) return;
    
        setTimeout(() => {
            document
                .getElementById("experience-options-heading")
                ?.focus();
    
            state.liveAnnouncement.value = "";
    
            setTimeout(() => {
                state.liveAnnouncement.value =
                    "Choose your reservation type. Select Standard Reservation, or choose an available experience if one is offered.";
            }, 100);
        }, 100);
    
    }, [props.isVisible, selectedExperience.value]);

    useEffect(() => {
        if (!props.isVisible) return;
        if (selectedExperience.value === undefined) return;
    
        const host = window.location.hostname.toLowerCase();
    
        const isCanada =
            host === "moxies.ca" ||
            host === "www.moxies.ca" ||
            host === "moxies-canada.ssdev.ca";
    
        const country = isCanada ? "CA" : "US";
        const label = isCanada ? "Canada" : "United States";
    
        let attempts = 0;
        const maxAttempts = 20;
    
        const applyCountry = () => {
            const wrapper = document.querySelector(
                ".mx-form__fieldset--phone-special mx-select"
            );
    
            if (!wrapper) {
                if (attempts++ < maxAttempts) {
                    requestAnimationFrame(applyCountry);
                }
                return;
            }
    
            const select = wrapper.querySelector('select[name="phone_number_code"]');
            const buttonLabel = wrapper.querySelector("[mx-select-label]");
            const hiddenInput = wrapper.querySelector('input[name="phone_number_code"]');
    
            if (!select) {
                if (attempts++ < maxAttempts) {
                    requestAnimationFrame(applyCountry);
                }
                return;
            }
    
            select.value = country;
            select.setAttribute("value", country);
            wrapper.setAttribute("value", country);
    
            if (hiddenInput) hiddenInput.value = country;
            if (buttonLabel) buttonLabel.textContent = label;
    
            select.dispatchEvent(new Event("change", { bubbles: true }));
            select.dispatchEvent(new Event("input", { bubbles: true }));
        };
    
        requestAnimationFrame(applyCountry);
    }, [props.isVisible, selectedExperience.value]);



    useEffect(() => {
        if (!state.selectedLocationRid.value || !props.isVisible || !state.selectedTime.value || !state.experienceIDs.value.length) return;

        isFetchingExperiences.value = true;

        fetchOTExperiences(state.selectedLocationRid.value)
            .then((data) => {
                const allExperiences = data?.data ?? [];

                const filteredExperiences = allExperiences.filter((exp) => {
                    return state.experienceIDs.value.includes(exp.experience_id.toString());
                });

                experiences.value = filteredExperiences;
            })
            .catch(() => {
                experiences.value = [];
            })
            .finally(() => {
                isFetchingExperiences.value = false;
            });
    }, [state.selectedLocationRid.value, state.selectedTime.value, state.experienceIDs.value, props.isVisible]);

    /**
     * @param {import("preact").JSX.TargetedSubmitEvent<HTMLFormElement>} event
     */
    const handleSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        const formEntries = /** @type {DetailFormEntries} */ (
            Object.fromEntries(formData)
        );

        /** @type OTConfirmPayload */
        let payload = {
            ...formEntries,
            rid: state.selectedLocationRid.value,
            reservation_token: state.reservationToken.value,
            reservation_attribute: state.selectedSeating.value,
            is_third_party: false,
            experience_id: selectedExperience.value?.experience_id ?? null,
            experience_version: selectedExperience.value?.version ?? null,
            add_ons: [],
            credit_card: null,
        };
        try {
            isError.value = false;
            isLoading.value = true;
            const json = await createReservation(payload);

            // Show the confirmation screen
            state.confirmedReservation.value = json;

            // Wait for Preact to render it, then reset scrolling
            requestAnimationFrame(() => {
                document
                    .querySelectorAll(".moxies-opentable-content")
                    .forEach(el => {
                        el.scrollTop = 0;
                    });

                const dialog = document.querySelector(".dialog-message.dialog-lightbox-message");
                if (dialog) {
                    dialog.scrollTop = 0;
                }
            });

            isLoading.value = false;

            
            const rDate = new Date(state.confirmedReservation.value?.date_time);
            const reservationType = formatReservationType(
                state.selectedSeating.value
            );
            trackEvent({
                event: "opentable reservation",
                reservationLocation: state.selectedRestaurant.value.title.rendered,
                reservationSize: json.party_size,
                reservationType,
                opentableReservationDate: rDate.toLocaleDateString(["en-CA", "en-US"]),
                opentableReservationTime: rDate.toLocaleTimeString(["en-CA", "en-US"], {
                    hour: "2-digit",
                    minute: "2-digit",
                }),
            });
        } catch (err) {
            isLoading.value = false;
            isError.value = true;
        }
    };

    const handleCancel = () => {
        batch(() => {
            selectedExperience.value = undefined;
            state.pendingTimer.value = null;
            state.lockedReservation.value = null;
            state.reservationToken.value = "";
            state.selectedTime.value = "";
            state.selectedSeating.value = "";
            state.isNextStepVisible.value = false;
        });
    };

    /**
     * @param {Event} event
     */
    const handleTelChange = (event) => {
        const input = /** @type {HTMLInputElement} */ (event.target);
        if (input.checkValidity()) {
            const formattedNumber = formatPhoneNumber(input.value.trim());
            input.setRangeText(formattedNumber, 0, input.value.length, "end");
        }
    };

    const renderExperiencesListSection = () => {
        if (selectedExperience.value !== undefined || !props.isVisible) {
            return null;
        }

        if (isFetchingExperiences.value) {
            return html`<div 
            class="mx-experiences__loading"
            role="status"
            aria-live="polite">
                        <mx-spinner></mx-spinner>
                        <span>  Loading experiences options...</span>
                    </div>`;
        }

        if (isLoading.value) {
            return html`<div class="mx-experiences__loading" 
            role="status" aria-live="polite">
                        <mx-spinner></mx-spinner>
                        <span>  Loading experience details...</span>
                    </div>`;
        }
        

        return html`
    <section
        class="mx-experiences-container__cards"
        aria-labelledby="experience-options-heading"
    >
        <h5
            id="experience-options-heading"
            class="mx-experiences__card-action-title"
            tabindex="-1"
        >
        Choose your reservation type
        </h5>

        ${experiences.value.map((exp) => html`
            <div
                class="mx-experiences__card"
                key=${exp.experience_id}
            >
                <div class="mx-experiences__card-content">
                <h4
                id=${`experience-title-${exp.experience_id}`}
                class="mx-experiences__card-title"
            >
                ${exp.name}
            </h4>

                    <p class="mx-experiences__card-description">
                        ${exp.description}
                    </p>
                </div>

                <div class="mx-experiences__card-actions">
                <${BaseButton}
                classes=${["mx-select-card-button"]}
                type="button"
                size="md"
                disabled=${isLoading.value}
                aria-label=${`Select ${exp.name}`}
                onClick=${() => lockWithExperience(exp)}
            >
                SELECT
            </${BaseButton}>
                </div>
            </div>
        `)}

        <div class="mx-experiences__card">
            <div class="mx-experiences__card-content">
                <h4 class="mx-experiences__card-title">
                    Standard Reservation
                </h4>

                <p class="mx-experiences__card-description">
                    Dine without selecting a special experience.
                </p>
            </div>

            <div class="mx-experiences__card-actions">
                    <${BaseButton}
                    classes=${["mx-select-card-button"]}
                    type="button"
                    size="md"
                    disabled=${isLoading.value}
                    aria-label="Select Standard Reservation"
                    onClick=${() => lockWithExperience(null)}
                >
                    SELECT
                </${BaseButton}>
            </div>
        </div>
    </section>
`;
    };

    return html`
    <${LiveAnnouncer} />
        <div
            ref=${detailsRef}
            class="mx-details mx-reservation-form__step reservation-form-wrapper ${props.isVisible
            ? "mx-reservation-form__step--active"
            : ""}"
        >
            <header class="mx-reservation-form__header">
                    <div
                    class="reservation-step-toggle-details"
                    aria-label="Reservation progress: Details step"
                >
                    <span
                        class="form-section-option"
                        aria-hidden="true"
                    >
                        Date
                    </span>
                
                    <span
                        class="form-section-option"
                        aria-current="step"
                    >
                        Details
                    </span>
                </div>
                ${canShowForm && html`
                <p mx-details__timer-message class="mx-details__timer-message">
                    ${!state.pendingTimer.value && state.isExpired.value === false
                        ? "Setting up table..."
                        : ""}
            
                    ${state.pendingTimer.value && state.isExpired.value === false
                        ? html`Time remaining to complete your reservation: <${DetailsFormTimer}/>.`
                        : ""}
            
                    ${!state.pendingTimer.value && state.isExpired.value
                        ? "You can still try to complete your reservation, but this table may no longer be available."
                        : ""}
                </p>

            `}
                <${ReservationSummary}
                        date=${state.selectedTimeDate.value}
                        partySize=${state.partySize.value}
                        title=${state.selectedRestaurant.value.title.rendered}
                />
                ${isError.value
                ? html`<p class="mx-form__error" role="alert">
                            Unable to submit reservation request. Please try again.
                        </p>`
                : null}
            </header>


            <!-- SCROLL CONTAINER --> <div class="moxies-opentable-content"> <!-- TITLE --> <div class="moxies-title"> <h4 class="elementor-heading-title moxies-heading"> <span class="moxies-title-default">Moxies</span> <span class="moxies-italic moxies-span"> ${restaurantTitle} </span> </h4> </div> <!-- ADDRESS --> <p class="mx-reservation-form__address"> ${restaurantAddress} </p>
            ${renderExperiencesListSection()}

            ${selectedExperience.value != null && html`
                <div class="mx-selected-experience">
                    <h5 class="mx-selected-experience__title">
                        Experience Summary
                    </h5>

                    <div class="mx-selected-experience__content">
                        <h6 class="mx-selected-experience__name">
                            ${selectedExperience.value.name}:
                        </h6>
                        <p class="mx-selected-experience__description">
                            ${state.partySize.value} ×
                        </p>
                    </div>
                </div>
            `}

            ${(canShowForm) && html`
                <div>
                    <form
                        id="opentable-step2"
                        class="mx-form mx-form--details"
                        onSubmit=${handleSubmit}
                    >
                        <h3 class="mx-form-title">
                        Your Details
                    </h3>

                        <div class="form-group">
                            <label for="first_name" class="form-label">* First Name</label>

                            <fieldset class="form-fieldset">
                                <input
                                    id="first_name"
                                    class="form-input"
                                    required
                                    disabled=${isLoading.value}
                                    type="text"
                                    placeholder="First Name"
                                    name="first_name"
                                />
                            </fieldset>
                        </div>

                        <div class="form-group">
                            <label for="last_name" class="form-label">* Last Name</label>

                            <fieldset>
                                <input
                                    id="last_name"
                                    required
                                    type="text"
                                    disabled=${isLoading.value}
                                    placeholder="Last Name"
                                    name="last_name"
                                    autocomplete="family-name"
                                />
                            </fieldset>
                        </div>

                        <div class="form-group">
                            <label for="phone_number" class="form-label">* Phone</label>

                            <fieldset class="mx-form__fieldset--phone mx-form__fieldset--phone-special"
                                      disabled=${isLoading.value}>

                                <mx-select>
                                    <select name="phone_number_code" value="CA" aria-label="Phone country">
                                        <option value="CA">Canada</option>
                                        <option value="US">United States</option>
                                    </select>
                                </mx-select>

                                <input
                                    id="phone_number"
                                    autocomplete="tel"
                                    required
                                    type="tel"
                                    title="Phone numbers must be 10 numbers long"
                                    placeholder="123 123 1234"
                                    name="phone_number"
                                    pattern="^(1\\s?)?(\\d{3}|\\(\\d{3}\\))[\\s\\-]?\\d{3}[\\s\\-]?\\d{4}$"
                                    onInput=${handleTelChange}
                                />
                            </fieldset>
                        </div>

                        <div class="form-group">
                            <label for="email_address" class="form-label">* Email Address</label>

                            <fieldset>
                                <input
                                    id="email_address"
                                    autocomplete="email"
                                    required
                                    type="email"
                                    disabled=${isLoading.value}
                                    title="Must be a valid email"
                                    placeholder="Email Address"
                                    name="email_address"
                                    pattern="[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}"
                                />
                            </fieldset>
                        </div>

                        <div class="form-group">
                            <label for="reservation_attribute" class="form-label"> Occasion (Optional)</label>

                            <fieldset class="mx-form__fieldset--occassion">
                                <mx-select>
                                    <select
                                        id="reservation_attribute"
                                        name="reservation_attribute"
                                        disabled=${isLoading.value}
                                        value=""
                                    >
                                        <option value="">Select an occasion</option>
                                        <option value="birthday">Birthday</option>
                                        <option value="anniversary">Anniversary</option>
                                        <option value="business_meal">Business Meal</option>
                                        <option value="celebration">Celebration</option>
                                    </select>
                                </mx-select>
                            </fieldset>
                        </div>

                        <div class="form-group">
                            <label for="special_request" class="form-label">Special Request (Optional)</label>

                            <fieldset>
                                <textarea
                                    id="special_request"
                                    resize="false"
                                    disabled=${isLoading.value}
                                    name="special_request"
                                    cols="100"
                                    rows="1"
                                    placeholder="Special request"
                                >
                                
                                </textarea>
                            </fieldset>
                        </div>

                        <fieldset class="mx-details__optin">
                            <label class="mx-form__checkbox">
                                <input
                                    type="checkbox"
                                    name="restaurant_email_marketing_opt_in"
                                    disabled=${isLoading.value}
                                />
                                Sign me up to receive dining offers and news from Moxies by email.
                            </label>
                        </fieldset>

                        <div class="mx-form__actions">
                            <${BaseButton}
                                icon=${settings.svgCalendarIcon}
                                type="submit"
                                isLoading=${isLoading.value}
                                size="lg"
                            >Complete Reservation
                            </
                            /
                            >
                            <${BaseButton}
                                    icon=${settings.svgArrowIcon}
                                    theme="white"
                                    onClick=${handleCancel}
                                    disabled=${selectedExperience.value === undefined || isLoading.value}
                                    size="lg"
                            >Reset
                            </
                            /
                            >
                        </div>
                    </form>
                </div>
            `}

            </div>
        </div>
    `;
}

export default DetailsForm;